import { useState } from "react";

/**
 * Map partner name → primary domain. Used to fetch favicons reliably.
 * Add new partners here as the affiliate mix grows.
 */
const PARTNER_DOMAIN = {
  // Stays
  "Booking.com": "booking.com",
  "Booking": "booking.com",
  "Trip.com": "trip.com",
  "Hotellook": "hotellook.com",
  "Agoda": "agoda.com",
  "Airbnb": "airbnb.co.in",
  "MakeMyTrip": "makemytrip.com",
  "Cleartrip": "cleartrip.com",
  // Flights
  "Aviasales": "aviasales.com",
  "WayAway": "wayaway.io",
  "Skyscanner": "skyscanner.co.in",
  "Google Flights": "google.com",
  "Google Hotels": "google.com",
  // Activities
  "GetYourGuide": "getyourguide.com",
  "Klook": "klook.com",
  "Tiqets": "tiqets.com",
  "Viator": "viator.com",
  // Food / maps
  "Google Maps": "google.com",
  "TripAdvisor": "tripadvisor.com",
};

const faviconUrl = (domain, sz = 64) =>
  `https://www.google.com/s2/favicons?domain=${domain}&sz=${sz}`;

/**
 * <PartnerLogo /> — renders the partner's favicon next to (or instead of) its name.
 *
 * Props:
 *   name        the partner display name (must match the keys above)
 *   size        favicon px (default 18)
 *   className   extra classes for the wrapping span
 *   showText    when true, render "<icon> <name>" — default true. Pass false for icon-only.
 */
export function PartnerLogo({ name, size = 18, className = "", showText = true }) {
  const domain = PARTNER_DOMAIN[name];
  const [errored, setErrored] = useState(false);

  if (!domain || errored) {
    // Fallback: render just the name with a colored initial chip
    return (
      <span className={`inline-flex items-center gap-1.5 ${className}`}>
        <span
          aria-hidden="true"
          className="grid place-items-center rounded-md font-bold text-white"
          style={{
            width: size,
            height: size,
            fontSize: Math.max(8, Math.floor(size * 0.55)),
            background:
              "linear-gradient(135deg, #2A7FB8 0%, #5FB3E3 100%)",
          }}
        >
          {(name || "?")[0]}
        </span>
        {showText && <span>{name}</span>}
      </span>
    );
  }

  return (
    <span className={`inline-flex items-center gap-1.5 ${className}`}>
      <img
        src={faviconUrl(domain, size * 2)}
        alt={`${name} logo`}
        loading="lazy"
        width={size}
        height={size}
        onError={() => setErrored(true)}
        className="rounded-sm flex-shrink-0 bg-white"
        style={{ width: size, height: size, objectFit: "contain" }}
      />
      {showText && <span>{name}</span>}
    </span>
  );
}
